import { Injectable } from '@jack-henry/frontend-utils/di';
import { JhHttpResponseType } from '@jack-henry/frontend-utils/http';
import {
    CompanyAccountModelDto,
    FilterAccountModelDto,
    GenerateReportClient,
    InformationReportsClient,
    ReportResultBaseModelDto,
    TransactionCodeGroupClient,
} from '@treasury/api/channel';
import { TmHttpClient } from '@treasury/core/http';
import { CompanyAccount } from '../company/company-account.entity';
import { downloadBlob } from '../utilities/file-handling';
import { ReportFilters } from './report-filters.entity';
import { ReportListItem } from './report-list-item.entity';
import { ReportMetaData } from './report-meta-data.entity';
import { ReportResultBaseModel } from './report-results.entity';

export enum ReportType {
    CurrentDayBalance = 'cdb',
    CurrentDayTransaction = 'cdt',
    PriorDayBalance = 'pdb',
    PriorDayTransaction = 'pdt',
    DateRangeBalance = 'drb',
    DateRangeTransaction = 'drt',
    Edi = 'edi',
}

export type TransactionCode = {
    name: string;
    isReadOnly: boolean;
    id: number;
    updatedBy: string;
    updatedDate: string;
};

export interface ReportResult extends ReportResultBaseModelDto {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    totalCheckingBalance: number;
    totalSavingsBalance: number;
    checkingTotalCount: number;
    savingTotalCount: number;
    totalTransactionCount: number;
    checkingTotalAmount: number;
    savingTotalAmount: number;
    totalTransactionAmount: number;
}

@Injectable()
export class ReportingService {
    constructor(
        private reportsClient: InformationReportsClient,
        private generateReportClient: GenerateReportClient,
        private transactionCodeGroupClient: TransactionCodeGroupClient
    ) {}

    public async getReports() {
        const response = await this.reportsClient.informationReportsGetAll2();
        return (
            response.data
                .map(report => new ReportListItem(report))
                .sort((a, b) => a.sequence - b.sequence)
                // Only show reports that are for the current day or prior day
                // product doesn't want to show date range reports until performance is improved
                .filter(
                    report => report.dateType === 'Prior Day' || report.dateType === 'Current Day'
                )
        );
    }

    public async updateFavoriteReportStatus(report: ReportListItem, isFavorite: boolean) {
        await this.reportsClient.informationReportsFavorite(report.id, { value: isFavorite });
    }

    public async deleteReport(report: ReportListItem) {
        await this.reportsClient.informationReportsDelete(report.id);
    }

    public async saveReport(report: ReportMetaData, filters: ReportFilters) {
        const response = await this.reportsClient.informationReportsPost(filters.toDtoMapped());
        return new ReportListItem(response.data);
    }

    public async updateReport(report: ReportMetaData, filters: ReportFilters) {
        await this.reportsClient.informationReportsPut(report.id, filters.toDtoMapped());
    }

    public async runReport(filter: ReportFilters) {
        const filterDto = filter.toDtoMapped();
        const response = await this.generateReportClient.generateReportPost(
            { type: filter.typeRequest },
            filterDto
        ); /**
         * reports that sometimes run return a null response - not sure where the failure is happening, `null` is handled in channel
         * as 'not an error'
         */
        return new ReportResultBaseModel(response.data as unknown as ReportResult);
    }

    public async downloadReport(
        model: ReportFilters,
        report: ReportResultBaseModel,
        downloadType: string
    ) {
        const downloadDto = report.downloadRequestDto;
        downloadDto.filterModel = model.toDtoMapped();
        downloadDto.downloadFormats = [downloadType === 'BAI2' ? 'PDF' : downloadType];
        // Current requests send strings, but the API expects an enum
        downloadDto.outputType = downloadType === 'BAI2' ? 'BAI' : downloadType;
        const http = await TmHttpClient.getInstance();
        const response = await http.request(`download/`, {
            method: 'POST',
            responseType: JhHttpResponseType.ArrayBuffer,
            body: downloadDto,
        });
        const type =
            downloadType.indexOf('PDF') >= 0 ? 'application/pdf' : 'application/octet-stream';
        const blob = new Blob([response], { type });
        const file =
            downloadDto.outputType === 'BAI'
                ? `${downloadDto.pageId}.txt`
                : `${downloadDto.pageId}.${downloadDto.outputType.toLowerCase()}`;
        return downloadBlob(blob, file);
    }

    // This has to be hand rolled since the casing in our
    // generated clients doesn't support TitleCase
    public async getReportAccounts(type: string) {
        const request = (await TmHttpClient.getInstance()).request<FilterAccountModelDto[]>(
            `companyaccounts/GetForProductFeature?productFeatureType=InformationReporting&reportType=${type}&type=`,
            {
                method: 'GET',
            }
        );
        const response = await request;
        return response.map(acc => new CompanyAccount(acc as unknown as CompanyAccountModelDto));
    }

    public async getReportTransactionCodes() {
        const response = await this.transactionCodeGroupClient.transactionCodeGroupGetAll();
        return response.data as TransactionCode[];
    }

    public async getReportMetaDataAndFilters(reportId: string) {
        const id = parseInt(reportId, 10);
        const response = await this.reportsClient.informationReportsGet(id);
        return {
            metaData: new ReportMetaData(response.data),
            filters: new ReportFilters(response.data),
        };
    }
}
